import { useCallback, useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

const LONG_PRESS_MS = 5000;
const TAP_COUNT = 3;
const TAP_WINDOW_MS = 900;

/**
 * Hidden technician gesture: a 5 s long-press **or** a quick triple-tap on the element fires
 * `onTrigger`. Returns pointer handlers to spread on the target element.
 */
export function useSecretGesture(onTrigger: () => void) {
  const pressTimer = useRef<number | undefined>(undefined);
  const taps = useRef<number[]>([]);

  const cancel = useCallback(() => {
    window.clearTimeout(pressTimer.current);
    pressTimer.current = undefined;
  }, []);

  useEffect(() => cancel, [cancel]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      cancel();
      pressTimer.current = window.setTimeout(() => {
        pressTimer.current = undefined;
        taps.current = [];
        onTrigger();
      }, LONG_PRESS_MS);

      const now = Date.now();
      taps.current = [...taps.current.filter((t) => now - t < TAP_WINDOW_MS), now];
      if (taps.current.length >= TAP_COUNT) {
        taps.current = [];
        cancel();
        onTrigger();
      }
    },
    [cancel, onTrigger],
  );

  return {
    onPointerDown,
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
  };
}
